import React from 'react';
import {useForm} from 'react-hook-form';
import {useSearchParams} from 'react-router-dom';
import {DateRange} from 'rsuite/DateRangePicker';
import {t} from '@lingui/core/macro';

import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import InputText from 'shared/ui/Input/InputText';
import Select from 'shared/ui/Select/Select';
import RangeInputs from 'shared/ui/Form/RangeInputs/RangeInputs';
import DateRangePickerControlled from 'shared/ui/Date/DateRangePickerControlled/DateRangePickerControlled';
import ListFiltersForm from 'shared/ui/Modal/ListFiltersForm/ListFiltersForm';
import {IFiltersFormComponentProps} from 'shared/ui/Modal/ListFiltersModal/ListFiltersModal';
import {dateRangeToFilters, getUrlDateRange, toNumberFilter, UrlFiltersData} from 'shared/lib/filterHelper';
import {getYesNoOptions} from 'shared/lib/filterOptions';
import {tariffAPI} from 'entities/tariff';

import {PaymentStatus} from '../../model/PaymentStatus';
import {translatePaymentStatus} from '../../lib/paymentStatus';

const PAYMENT_STATUSES = ['created', 'paid', 'canceled'] as PaymentStatus[];

interface IPaymentsFiltersForm {
    number?: string,
    status?: string,
    tariff?: string,
    recurring?: string,
    validRecurring?: string,
    auto?: string,
    sumFrom?: string,
    sumTo?: string,
    createdAt?: DateRange,
    payedAt?: DateRange,
}

interface IPaymentsFiltersFormProps extends IFiltersFormComponentProps {
    admin?: boolean,
}

const PaymentsFiltersForm: React.FC<IPaymentsFiltersFormProps> = ({onClose, admin}) => {
    const [searchParams] = useSearchParams();
    const {data: tariffs} = tariffAPI.useGetTariffsQuery({field: 'price', direction: 'asc'});

    const {register, handleSubmit, control} = useForm<IPaymentsFiltersForm>({
        defaultValues: {
            number: searchParams.get('number') ?? undefined,
            status: searchParams.get('status') ?? undefined,
            tariff: searchParams.get('tariff.id') ?? undefined,
            recurring: searchParams.get('recurring') ?? undefined,
            validRecurring: searchParams.get('valid_recurring') ?? undefined,
            auto: searchParams.get('auto') ?? undefined,
            sumFrom: searchParams.get('sum[gte]') ?? undefined,
            sumTo: searchParams.get('sum[lte]') ?? undefined,
            createdAt: getUrlDateRange(searchParams, 'created_at'),
            payedAt: getUrlDateRange(searchParams, 'payed_at'),
        },
    });

    const statusOptions = PAYMENT_STATUSES.map(status => ({label: translatePaymentStatus(status), value: status}));
    const tariffOptions = (tariffs ?? []).map(tariff => ({label: tariff.name, value: String(tariff.id)}));
    const autoOptions = [
        {label: t`Автоплатёж`, value: 'true'},
        {label: t`Ручная оплата`, value: 'false'},
    ];

    const toFilters = ({
        number, status, tariff, recurring, validRecurring, auto, sumFrom, sumTo, createdAt, payedAt,
    }: IPaymentsFiltersForm) => {
        const data: UrlFiltersData = {
            status: status ?? undefined,
            'tariff.id': tariff ?? undefined,
            recurring: recurring ?? undefined,
            auto: auto ?? undefined,
            'sum[gte]': toNumberFilter(sumFrom),
            'sum[lte]': toNumberFilter(sumTo),
        };
        if (admin) {
            data.number = number?.trim() || undefined;
            data.valid_recurring = validRecurring ?? undefined;
        }
        dateRangeToFilters(createdAt, data, 'created_at');
        dateRangeToFilters(payedAt, data, 'payed_at');

        return data;
    };

    return (
        <ListFiltersForm handleSubmit={handleSubmit} toFilters={toFilters} onClose={onClose}>
            {admin && (
                <FormGroupWithError label={t`Номер`}>
                    <InputText register={register} control={control} name="number"/>
                </FormGroupWithError>
            )}

            <FormGroupWithError label={t`Статус`}>
                <Select control={control} name="status" data={statusOptions} cleanable={true} searchable={false}/>
            </FormGroupWithError>

            <FormGroupWithError label={t`Тариф`}>
                <Select
                    control={control}
                    name="tariff"
                    data={tariffOptions}
                    cleanable={true}
                    searchable={tariffOptions.length > 5}
                />
            </FormGroupWithError>

            <FormGroupWithError label={t`Вид оплаты`}>
                <Select control={control} name="auto" data={autoOptions} cleanable={true} searchable={false}/>
            </FormGroupWithError>

            <FormGroupWithError label={t`Автопродление`}>
                <Select
                    control={control}
                    name="recurring"
                    data={getYesNoOptions()}
                    cleanable={true}
                    searchable={false}
                />
            </FormGroupWithError>

            {admin && (
                <FormGroupWithError label={t`Действующее автопродление`}>
                    <Select
                        control={control}
                        name="validRecurring"
                        data={getYesNoOptions()}
                        cleanable={true}
                        searchable={false}
                    />
                </FormGroupWithError>
            )}

            <FormGroupWithError label={t`Сумма`}>
                <RangeInputs register={register} control={control} fromName="sumFrom" toName="sumTo"/>
            </FormGroupWithError>

            <FormGroupWithError label={t`Период создания`}>
                <DateRangePickerControlled control={control} name="createdAt" cleanable={true}/>
            </FormGroupWithError>

            <FormGroupWithError label={t`Период оплаты`}>
                <DateRangePickerControlled control={control} name="payedAt" cleanable={true}/>
            </FormGroupWithError>
        </ListFiltersForm>
    );
};

export default PaymentsFiltersForm;
